import type { ReactNode } from 'react';
import type React from 'react';
import { Component } from 'react';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type State = {
  hasError: boolean;
};

/**
 * React error boundary that isolates the Dutchie Intelligence Service subtree.
 *
 * - Catches render and lifecycle errors from its children
 * - Prevents a Dutchie Intelligence Service failure from crashing the rest of the app
 * - Optionally renders a `fallback` UI and invokes `onError`
 *
 * Props:
 * - `children`: Subtree to protect with the boundary
 * - `fallback?`: UI to show after an error (defaults to `null`)
 * - `onError?`: Callback invoked with `(error, errorInfo)` in production and dev
 *
 * @example
 * ```tsx
 * <DutchieIntelligenceServiceErrorBoundary fallback={<div>Service unavailable</div>}>
 *   <DutchieIntelligenceProvider>
 *     <ServiceContextBridges />
 *     <YourApp />
 *   </DutchieIntelligenceProvider>
 * </DutchieIntelligenceServiceErrorBoundary>
 * ```
 */
export class DutchieIntelligenceServiceErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error(
        '[DutchieIntelligenceServiceErrorBoundary] Caught error in Dutchie Intelligence Service:',
        error,
        errorInfo
      );
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // In production, log to error tracking service
    // TODO: Add DataDog or similar error tracking
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI if provided, otherwise render nothing
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}
